import { Add, ImportExport } from '@mui/icons-material';
import { Box, Button, Dialog, DialogContent, DialogTitle, IconButton as MuiIconButton, Paper, Theme, Typography, useMediaQuery } from '@mui/material';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import React, { useEffect, useState } from 'react';
import { MdClose as CloseIcon } from 'react-icons/md';
import {
  Screening,
  listScreenings,
  resetPeopleState
} from '../../../slices/payrolslices/PeopleSlice';
import AddPeopleModal from './AddPeopleModal';
import EmployeeDetailsModal from './EmployeeDetails/EmployeeDetailsModal';
import EmployeeSummaryModal from './EmployeeSummaryModal';
import ImportFile from './ImportFile';
import PeopleList from './PeopleList';

const PeoplePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const [importOpen, setImportOpen] = useState(false);
  const [importedFile, setImportedFile] = useState<File | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [summaryModalOpen, setSummaryModalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<Screening | null>(null);
  const { success, message } = useAppSelector((state) => state.people);
  const selectedProjectId = useAppSelector((state) => state.homeTilesServices.selectedProjectId);
  const selectedServiceTitle = useAppSelector(
    (state) => state.serviceSelected.selectedServiceTitle
  );

  const isSmallScreen = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));
  const isMediumScreen = useMediaQuery((theme: Theme) => theme.breakpoints.between('sm', 'md'));

  // Refresh list on successful addPeople
  useEffect(() => {
    if (
      success &&
      message === 'People added successfully' &&
      selectedProjectId &&
      selectedServiceTitle
    ) {
      dispatch(
        listScreenings({
          group_id: selectedProjectId,
          service_id: selectedServiceTitle,
          page_size: 10
        })
      );
      dispatch(resetPeopleState());
    }
  }, [success, message, selectedProjectId, selectedServiceTitle, dispatch]);

  const handleAddDetails = (screening: Screening) => {
    setSelectedEmployee(screening);
    setDetailsModalOpen(true);
  };

  // Font size scaling
  const getFontSize = (small: string, medium: string, large: string) => {
    if (isSmallScreen) return small;
    if (isMediumScreen) return medium;
    return large;
  };

  // Drag and drop handlers
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      setImportedFile(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setImportedFile(e.target.files[0]);
    }
  };

  const handleImportModalClose = () => {
    setImportOpen(false);
    setImportedFile(null);
  };

  const handleAddPeople = () => {
    setAddModalOpen(true);
  };

  return (
    <Paper
      sx={{
        p: isSmallScreen ? 1 : 1,
        minHeight: '100vh',
        borderRadius: 2,
        width: '100%',
        overflowX: 'auto'
      }}
    >
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isSmallScreen ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          gap: 1
        }}
      >
        <Typography
          variant="h6"
          fontWeight="bold"
          sx={{
            fontSize: getFontSize('1rem', '1.1rem', '1.25rem'),
            color: 'text.primary'
          }}
        >
          People Management
        </Typography>

        <Box
          sx={{
            display: 'flex',
            gap: 1,
            width: isSmallScreen ? '100%' : 'auto'
          }}
        >
          <Button
            variant="outlined"
            startIcon={<ImportExport />}
            size={isSmallScreen ? 'small' : 'medium'}
            onClick={() => setImportOpen(true)}
          >
            Import
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddPeople}
            size={isSmallScreen ? 'small' : 'medium'}
          >
            Add Record
          </Button>
        </Box>
      </Box>

      {/* People List */}
      <PeopleList
        viewMode="table"
        pageSize={10}
        onAddDetails={handleAddDetails}
        getFontSize={getFontSize}
        isSmallScreen={isSmallScreen}
        isMediumScreen={isMediumScreen}
        setDetailsModalOpen={setDetailsModalOpen}
        setSummaryModalOpen={setSummaryModalOpen}
        setSelectedEmployee={setSelectedEmployee}
      />

      {/* Import Modal */}
      <Dialog
        open={importOpen}
        onClose={handleImportModalClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3
          }
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pb: 1,
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            Import People Data
          </Typography>
          <MuiIconButton onClick={handleImportModalClose} size="small">
            <CloseIcon size={20} />
          </MuiIconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 3, pb: 1 }}>
          <ImportFile
            importedFile={importedFile}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onFileChange={handleFileChange}
            onClick={() => document.getElementById('import-file-input')?.click()}
            isSmallScreen={isSmallScreen}
            getFontSize={getFontSize}
          />
        </DialogContent>
      </Dialog>

      {/* Add People Modal */}
      <AddPeopleModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onAdd={(payload) => {
          console.log('People added:', payload);
          // setAddModalOpen(false);
        }}
      />

      {/* Employee Details Modal */}
      <EmployeeDetailsModal
        open={detailsModalOpen}
        onClose={() => {
          setDetailsModalOpen(false);
          setSelectedEmployee(null);
        }}
        screening={selectedEmployee}
      />

      <EmployeeSummaryModal
        open={summaryModalOpen}
        onClose={() => setSummaryModalOpen(false)}
        screening={selectedEmployee}
        onAddDetails={handleAddDetails}
        setDetailsModalOpen={setDetailsModalOpen}
      />
    </Paper>
  );
};

export default PeoplePage;
